import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import { useTheme } from "../contexts/ThemeContext";
import { Flame, User as UserIcon, LogOut, Package, MapPin, LayoutDashboard, Boxes, Truck, Tag, MessageCircle, Building2, BarChart3, ShoppingBag, Droplets, ArrowRight, Moon, Sun, Star } from "lucide-react";
import { Button } from "./ui/button";
import { BottomSheet } from "./ui/bottom-sheet";
import { api } from "../lib/api";

export default function TopBar() {
  const { user, logout } = useAuth();
  const { currentCompany, platformName } = useCompany();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  // BottomSheet "Empezar pedido" — espejo del flujo del Home / Profile.
  // Permite que el cliente arranque un pedido desde el menú desplegable
  // del usuario (foto/nombre) sin tener que volver al Home.
  const [chooseOpen, setChooseOpen] = useState(false);

  // Conteo de pedidos pendientes (status="pending") para el badge en
  // el menú admin. Sólo se carga para `company_admin` y se refresca
  // cada vez que se abre el menú — así el admin ve cuántos pedidos
  // están sin atender desde cualquier subpágina sin recargar.
  const [pendingCount, setPendingCount] = useState(null);
  useEffect(() => {
    if (user?.role !== "company_admin" || !menuOpen) return;
    let cancelled = false;
    api
      .get("/orders/admin", { params: { page: 1, page_size: 1, status: "pending" } })
      .then(({ data }) => { if (!cancelled) setPendingCount(data?.total ?? 0); })
      .catch(() => { /* silencioso */ });
    return () => { cancelled = true; };
  }, [menuOpen, user?.role]);

  // Where the brand-link should land. Sending an authenticated user to the
  // public Landing page when they click the logo causes a layout swap that
  // the user perceives as a "blink". Route to the relevant home for their
  // role instead, so navigation stays inside the same app shell.
  const brandHref = !user
    ? "/"
    : user.role === "super_admin" ? "/super-admin"
    : user.role === "company_admin" ? "/admin"
    : "/app";

  const onLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate("/");
  };
  const go = (to) => {
    setMenuOpen(false);
    navigate(to);
  };

  return (
    <header
      className="sticky top-0 z-40 bg-white/85 backdrop-blur-lg border-b border-neutral-200"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-6 h-16">
        <Link
          to={brandHref}
          className="group flex items-center gap-2 select-none outline-none focus:outline-none focus-visible:outline-none [-webkit-tap-highlight-color:transparent]"
          data-testid="brand-link"
        >
          {/* Branding FIJO de plataforma: logo 3D + "Gas2GO" siempre,
              independiente de si hay usuario autenticado o de la empresa
              actualmente seleccionada. Esto refuerza que Gas2GO es el
              paraguas que agrupa a todas las empresas.
              El branding de la empresa específica aparece únicamente en:
                - El chip "Comprando en <empresa>" del Home
                - La cabecera de productos dentro del wizard
                - Emails transaccionales (branding completo de empresa)
              En el resto de la app el chrome es siempre Gas2GO. */}
          <img
            src="/pwa-192.png"
            alt={platformName}
            loading="eager"
            decoding="sync"
            className="h-9 w-9 rounded-[10px] object-cover transition-transform duration-200 ease-out group-hover:scale-105 group-active:scale-95"
          />
          <span className="font-display font-extrabold tracking-tight text-lg">
            {platformName}
          </span>
        </Link>

        <nav className="flex items-center gap-2">
          {user ? (
            <>
              {user.role === "client" && (
                <>
                  <Button variant="ghost" asChild className="hidden sm:flex" data-testid="nav-orders">
                    <Link to="/app/orders"><Package size={16} className="mr-1.5" />Mis pedidos</Link>
                  </Button>
                  <Button variant="ghost" asChild className="hidden sm:flex" data-testid="nav-addresses">
                    <Link to="/app/addresses"><MapPin size={16} className="mr-1.5" />Direcciones</Link>
                  </Button>
                </>
              )}
              {user.role === "super_admin" && (
                <Button variant="ghost" asChild data-testid="nav-superadmin">
                  <Link to="/super-admin">Super Admin</Link>
                </Button>
              )}
              <button
                type="button"
                onClick={() => setMenuOpen(true)}
                className="flex items-center gap-2 rounded-full border border-neutral-200 pl-2 pr-3 py-1.5 hover:bg-neutral-50 tap-fade"
                data-testid="user-menu"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-7 w-7 rounded-full" />
                ) : (
                  <div className="h-7 w-7 rounded-full bg-company text-[color:var(--company-primary-contrast)] flex items-center justify-center text-xs">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                )}
                <span className="text-sm font-medium hidden md:inline">{user.name}</span>
              </button>

              <BottomSheet
                open={menuOpen}
                onOpenChange={setMenuOpen}
                testid="user-menu-sheet"
              >
                <div className="px-6 pt-2 pb-4 flex flex-col items-center text-center">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-16 w-16 rounded-full" />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-company text-[color:var(--company-primary-contrast)] flex items-center justify-center text-xl font-semibold">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </div>
                  )}
                  <h2 className="mt-3 font-display text-xl font-extrabold leading-tight">
                    {user.name || "Usuario"}
                  </h2>
                  <p className="text-sm text-neutral-500 truncate max-w-full">{user.email}</p>
                </div>

                <div className="px-3 pb-4 max-w-md mx-auto w-full">
                  {user.role === "client" && (
                    <>
                      <MenuRow
                        testid="menu-profile"
                        icon={<UserIcon size={18} />}
                        label="Mi perfil"
                        onClick={() => go("/app/profile")}
                      />
                      <MenuRow
                        testid="menu-orders"
                        icon={<Package size={18} />}
                        label="Mis pedidos"
                        onClick={() => go("/app/orders")}
                      />
                      <MenuRow
                        testid="menu-addresses"
                        icon={<MapPin size={18} />}
                        label="Mis direcciones"
                        onClick={() => go("/app/addresses")}
                      />
                      <MenuRow
                        testid="menu-start-order"
                        icon={<ShoppingBag size={18} />}
                        label="Empezar pedido"
                        onClick={() => { setMenuOpen(false); setChooseOpen(true); }}
                        accent
                      />
                      <div className="h-px bg-neutral-100 my-2" />
                    </>
                  )}
                  {user.role === "company_admin" && (
                    <>
                      <MenuRow
                        testid="menu-admin-dashboard"
                        icon={<LayoutDashboard size={18} />}
                        label="Panel de control"
                        onClick={() => go("/admin")}
                      />
                      <MenuRow
                        testid="menu-admin-orders"
                        icon={<Package size={18} />}
                        label="Pedidos"
                        badge={pendingCount && pendingCount > 0 ? pendingCount : null}
                        onClick={() => go("/admin/orders")}
                      />
                      <MenuRow
                        testid="menu-admin-reports"
                        icon={<BarChart3 size={18} />}
                        label="Reportes"
                        onClick={() => go("/admin/reports")}
                      />
                      <MenuRow
                        testid="menu-admin-products"
                        icon={<Boxes size={18} />}
                        label="Productos"
                        onClick={() => go("/admin/products")}
                      />
                      <MenuRow
                        testid="menu-admin-drivers"
                        icon={<Truck size={18} />}
                        label="Unidades de Reparto"
                        onClick={() => go("/admin/drivers")}
                      />
                      <MenuRow
                        testid="menu-admin-offers"
                        icon={<Tag size={18} />}
                        label="Ofertas"
                        onClick={() => go("/admin/offers")}
                      />
                      <MenuRow
                        testid="menu-admin-wabot"
                        icon={<MessageCircle size={18} />}
                        label="WhatsApp Bot"
                        onClick={() => go("/admin/wabot")}
                      />
                      <MenuRow
                        testid="menu-admin-ratings"
                        icon={<Star size={18} />}
                        label="Calificaciones"
                        onClick={() => go("/admin/ratings")}
                      />
                      <MenuRow
                        testid="menu-admin-branding"
                        icon={<Building2 size={18} />}
                        label="Empresa"
                        onClick={() => go("/admin/branding")}
                      />
                      <div className="h-px bg-neutral-100 my-2" />
                    </>
                  )}
                  <MenuRow
                    testid="menu-theme-toggle"
                    icon={theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                    label={theme === "dark" ? "Modo claro" : "Modo oscuro"}
                    onClick={() => { toggleTheme(); }}
                  />
                  <MenuRow
                    testid="menu-logout"
                    icon={<LogOut size={18} className="text-red-500" />}
                    label="Cerrar sesión"
                    labelClass="text-red-600"
                    onClick={onLogout}
                  />
                </div>
              </BottomSheet>

              {/* Espejo del Home: BottomSheet de selección de canal
                  para el atajo "Empezar pedido" del menú de usuario. */}
              <BottomSheet
                open={chooseOpen}
                onOpenChange={setChooseOpen}
                title="¿Qué vas a pedir?"
                description="Elige el tipo de producto para continuar."
                testid="topbar-start-order-sheet"
              >
                <div className="px-6 pb-6">
                  <div className="mt-2 grid grid-cols-2 gap-3 max-w-2xl mx-auto">
                    <button
                      type="button"
                      onClick={() => { setChooseOpen(false); navigate("/app/order/new?kind=bulk&fresh=1"); }}
                      className="group rounded-2xl border border-neutral-200 p-5 text-left hover:border-company hover:shadow-md transition-all bg-white"
                      data-testid="topbar-sheet-choose-bulk"
                    >
                      <Droplets size={26} className="text-company" />
                      <p className="mt-3 font-display font-extrabold text-xl">Gas</p>
                      <p className="text-xs text-neutral-500 mt-0.5">Litros o kilogramos</p>
                      <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold">
                        Elegir <ArrowRight size={14} />
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setChooseOpen(false); navigate("/app/order/new?kind=cylinder&fresh=1"); }}
                      className="group rounded-2xl border border-neutral-200 p-5 text-left hover:border-company hover:shadow-md transition-all bg-white"
                      data-testid="topbar-sheet-choose-cylinder"
                    >
                      <Flame size={26} className="text-company" />
                      <p className="mt-3 font-display font-extrabold text-xl">Cilindros</p>
                      <p className="text-xs text-neutral-500 mt-0.5">20 / 35 / 45 kg</p>
                      <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold">
                        Elegir <ArrowRight size={14} />
                      </span>
                    </button>
                  </div>
                </div>
              </BottomSheet>
            </>
          ) : (
            <>
              <Button variant="ghost" asChild data-testid="nav-login">
                <Link to="/login"><UserIcon size={16} className="mr-1.5" />Entrar</Link>
              </Button>
              <Button asChild className="bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90" data-testid="nav-register">
                <Link to="/register">Crear cuenta</Link>
              </Button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

function MenuRow({ icon, label, labelClass = "", onClick, testid, accent = false, badge = null }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-colors text-left tap-fade ${
        accent
          ? "bg-company text-[color:var(--company-primary-contrast)] hover:opacity-90 mt-2"
          : "hover:bg-neutral-100 active:bg-neutral-200"
      }`}
    >
      <span className={`h-9 w-9 flex items-center justify-center rounded-lg ${
        accent ? "bg-white/20" : "bg-neutral-100 text-neutral-700"
      }`}>
        {icon}
      </span>
      <span className={`text-sm font-medium flex-1 ${labelClass}`}>{label}</span>
      {badge != null && (
        <span
          className="ml-2 inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full text-[11px] font-bold tabular-nums bg-amber-500 text-white shadow-sm animate-in fade-in"
          aria-label={`${badge} sin atender`}
          data-testid={`${testid}-badge`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  );
}
